import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useProperty,
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { formatPhone, plainText } from './propertyUtils';

const heroDef: SlotGroupDefinition = {
  label: 'Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
    },
  },
};

const websiteDef: SlotGroupDefinition = {
  label: 'Website CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Visit community website',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function CommunityLanding() {
  const property = useProperty() as Record<string, any>;
  const hero = useSlotGroup('communitylanding.hero', heroDef);
  const website = useSlotGroup('communitylanding.website', websiteDef);
  const name = plainText(useContentToken('communitylanding.name'));
  const city = plainText(useContentToken('communitylanding.city'));
  const state = plainText(useContentToken('communitylanding.state'));
  const phone = useContentToken('communitylanding.phone');
  const beds = plainText(useContentToken('communitylanding.bedrooms'));
  const price = plainText(useContentToken('communitylanding.price'));

  const location = [city, state].filter(Boolean).join(', ');
  const phoneHref = (phone || property.property_phone || '').replace(/\D/g, '');

  return (
    <article>
      <section className="relative min-h-[50vh] overflow-hidden">
        <EditableSlotGroup groupPath="communitylanding.hero" definition={heroDef}>
          <div className="absolute inset-0">
            <VibesitesImage
              url={hero.image || ''}
              alt={hero.alt || name || ''}
              priority
              sizes="100vw"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--vibe-color-primary)_55%,transparent)]" />
          </div>
        </EditableSlotGroup>
        <div className="relative z-10 mx-auto flex min-h-[50vh] max-w-5xl flex-col justify-end px-6 py-16">
          <RichTextContent
            path="communitylanding.name"
            label="Community name"
            as="h1"
            className="font-heading text-4xl text-primary-foreground md:text-5xl"
          />
          <p className="mt-3 font-body text-lg text-[color-mix(in_srgb,var(--vibe-color-text-on-primary)_90%,transparent)]">
            <RichTextContent path="communitylanding.city" label="City" as="span" />
            {city && state ? ', ' : ''}
            <RichTextContent path="communitylanding.state" label="State" as="span" />
          </p>
        </div>
      </section>

      <section className="bg-background py-16">
        <div className="mx-auto grid max-w-5xl gap-10 px-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <RichTextContent
              path="communitylanding.description"
              label="Description"
              as="div"
              className="font-body text-base leading-relaxed text-foreground [&_p]:mb-4"
            />
          </div>
          <aside className="rounded-lg border border-border bg-card p-6 text-card-foreground">
            <p className="font-heading text-lg">{location || name}</p>
            <p className="mt-2 font-body text-sm text-muted-foreground">
              <RichTextContent path="communitylanding.address" label="Address" as="span" />
            </p>
            <p className={phone ? 'mt-3 font-body text-sm' : 'hidden'}>
              <a href={phoneHref ? `tel:${phoneHref}` : undefined} className="text-primary">
                <EditableToken path="communitylanding.phone" as="span">
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </p>
            <p className="mt-4 font-body text-sm">
              <RichTextContent path="communitylanding.bedrooms" label="Bedrooms" as="span" />
              {beds ? ' bedrooms' : ''}
            </p>
            <p className="font-body text-sm">
              {price ? 'From $' : ''}
              <RichTextContent path="communitylanding.price" label="Price range" as="span" />
            </p>
            <EditableSlotGroup groupPath="communitylanding.website" definition={websiteDef}>
              <a
                href={website.url || '#'}
                target={website._target || '_blank'}
                rel={website._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 font-heading text-sm text-primary-foreground"
              >
                {website.label || 'Visit community website'}
              </a>
            </EditableSlotGroup>
          </aside>
        </div>
      </section>
    </article>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunityLanding',
  displayName: 'Community Landing',
  tokens: {
    'communitylanding.name': {
      label: 'Community name',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Community</p>',
    },
    'communitylanding.city': {
      label: 'City',
      type: 'richtext',
      defaultField: 'property_address_city',
      defaultValue: '',
    },
    'communitylanding.state': {
      label: 'State',
      type: 'richtext',
      defaultField: 'property_address_state',
      defaultValue: '',
    },
    'communitylanding.address': {
      label: 'Address',
      type: 'richtext',
      defaultField: 'property_address',
      defaultValue: '',
    },
    'communitylanding.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '',
    },
    'communitylanding.bedrooms': {
      label: 'Bedrooms',
      type: 'richtext',
      defaultField: 'property_bedrooms_range',
      defaultValue: '',
    },
    'communitylanding.price': {
      label: 'Price range',
      type: 'richtext',
      defaultField: 'property_price_range',
      defaultValue: '',
    },
    'communitylanding.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_short_description"></span></p>',
    },
  },
  slotGroups: {
    'communitylanding.hero': heroDef,
    'communitylanding.website': websiteDef,
  },
};

(CommunityLanding as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommunityLanding;
